import React, {useEffect, useRef, useState} from 'react'
import {Link, RouteComponentProps} from 'react-router-dom'
import {Trans} from 'react-i18next'
import {Button, Container, Typography} from '@material-ui/core'
import {backendGetRequest, backendRequest} from '../../common/utils/requests'

type Status = 'loading' | 'ready' | 'submitting' | 'success' | 'login' | 'error'

/** Shows the email-confirmed action and requires a click before opening an account. */
export function SecondPartnerAccount({token}: {token: string}) {
  const [status, setStatus] = useState<Status>('loading')
  const submitting = useRef(false)

  useEffect(() => {
    let active = true
    setStatus('loading')
    backendGetRequest(`/auth/second-partner-account?token=${encodeURIComponent(token)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error('invalidToken')
        const body = await response.json()
        if (active) setStatus(body.action === 'login' ? 'login' : 'ready')
      })
      .catch(() => { if (active) setStatus('error') })
    return () => { active = false }
  }, [token])

  async function confirm() {
    if (submitting.current || status !== 'ready') return
    submitting.current = true
    setStatus('submitting')
    try {
      const response = await backendRequest('/auth/second-partner-account/finish', {token})
      if (!response.ok) throw new Error('invalidToken')
      const body = await response.json()
      if (body.partnerLegalAcceptanceUrl) {
        // A Partner API registration in the iframe flow used to get this link in the
        // registration answer. The partner no longer gets one, so the client — who is
        // in the browser right now — goes to the legal acceptance page directly.
        window.location.assign(body.partnerLegalAcceptanceUrl)
        return
      }
      setStatus(body.action === 'login' ? 'login' : 'success')
    } catch {
      setStatus('error')
    } finally {
      submitting.current = false
    }
  }

  return (
    <Container maxWidth="sm" style={{paddingTop: 48}}>
      <Typography variant="h5" component="h1" gutterBottom>
        <Trans i18nKey="partnerAccountConfirmation" defaults="Confirm your registration" />
      </Typography>
      {status === 'loading' && <Typography><Trans i18nKey="loading" defaults="Loading…" /></Typography>}
      {status === 'error' && (
        <Typography role="alert" color="error">
          <Trans i18nKey="partnerAccountInvalidLink" defaults="This link is invalid or has expired. Please register again to request a new link." />
        </Typography>
      )}
      {status === 'success' && (
        <Typography role="status">
          <Trans i18nKey="partnerAccountConfirmed" defaults="Your account is ready. Log in to continue." />
        </Typography>
      )}
      {status === 'login' && (
        <Typography role="status">
          <Trans i18nKey="partnerAccountLogin" defaults="You already have an account with this partner. Log in to continue or open another account." />
        </Typography>
      )}
      {(status === 'ready' || status === 'submitting') && <>
        <Typography paragraph>
          <Trans i18nKey="partnerAccountConfirmDescription" defaults="Confirm to continue registration. If you already have a profile, your existing details and verification documents will be used." />
        </Typography>
        <Button variant="contained" color="primary" disabled={status === 'submitting'} onClick={confirm}>
          <Trans i18nKey="confirm" defaults="Confirm" />
        </Button>
      </>}
      {(status === 'success' || status === 'login' || status === 'error') && (
        <Button component={Link} to="/login" role="link" color="primary">
          <Trans i18nKey="login" defaults="Log in" />
        </Button>
      )}
    </Container>
  )
}

export default function SecondPartnerAccountRoute({match}: RouteComponentProps<{token: string}>) {
  return <SecondPartnerAccount token={match.params.token} />
}

/** Displays the same next step for every accepted registration. */
export function RegistrationPending() {
  return (
    <Container maxWidth="sm" style={{paddingTop: 48}}>
      <Typography variant="h5" component="h1" gutterBottom>
        <Trans i18nKey="registrationCheckEmail" defaults="Check your email" />
      </Typography>
      <Typography role="status">
        <Trans i18nKey="registrationCheckEmailDescription" defaults="Follow the link sent to your email address to continue registration. The link is valid for 24 hours. You can close this page." />
      </Typography>
    </Container>
  )
}
